import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import {
  Mail, Send, Users, ArrowLeft, History, BarChart3,
  UserCheck, UserX, Calendar, UserPlus, Bell
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';

import { AdminEmailProvider } from '../components/admin-email/AdminEmailContext';
import ComposeTab from '../components/admin-email/ComposeTab';
import OnboardingTab from '../components/admin-email/OnboardingTab';
import NudgesTab from '../components/admin-email/NudgesTab';
import HistoryTab from '../components/admin-email/HistoryTab';
import SchedulerTab from '../components/admin-email/SchedulerTab';
import RecipientsTab from '../components/admin-email/RecipientsTab';
import AnalyticsTab from '../components/admin-email/AnalyticsTab';
import ManualRecipientsModal from '../components/admin-email/ManualRecipientsModal';
import ScheduledRecipientsModal from '../components/admin-email/ScheduledRecipientsModal';
import '../components/admin-email/admin-email.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DRAFT_STORAGE_KEY = 'baadtv_email_draft';

const EMAIL_CATEGORIES = [
  { id: 'feature_announcement', label: 'Feature Announcement' },
  { id: 'general_announcement', label: 'General Announcement' },
  { id: 'coming_episodes', label: 'Coming Episodes' },
  { id: 'social_activity', label: 'Social Activity' }
];

const TABS = [
  { id: 'compose', label: 'Compose', icon: Mail },
  { id: 'onboarding', label: 'Onboarding', icon: UserPlus },
  { id: 'nudges', label: 'Nudges', icon: Bell },
  { id: 'scheduler', label: 'Scheduler', icon: Calendar },
  { id: 'history', label: 'History', icon: History },
  { id: 'recipients', label: 'Recipients', icon: Users },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
];

const AdminEmail = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('compose');
  const [emailStats, setEmailStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [triggeringJob, setTriggeringJob] = useState(null);
  const [scheduledPreview, setScheduledPreview] = useState(null);
  const [scheduledRecipients, setScheduledRecipients] = useState(null);
  const [scheduledFlagged, setScheduledFlagged] = useState(null);
  const [scheduledRecipientTab, setScheduledRecipientTab] = useState('active');
  const [loadingScheduledPreview, setLoadingScheduledPreview] = useState(null);
  const [loadingScheduledRecipients, setLoadingScheduledRecipients] = useState(null);
  const [sendingScheduledTest, setSendingScheduledTest] = useState(null);
  const [manualRecipients, setManualRecipients] = useState(null);
  const [manualFlagged, setManualFlagged] = useState(null);
  const [loadingManualRecipients, setLoadingManualRecipients] = useState(false);
  const [recipientTab, setRecipientTab] = useState('active');
  const [flaggingUser, setFlaggingUser] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Onboarding templates state
  const [onboardingTemplates, setOnboardingTemplates] = useState([]);
  const [loadingOnboarding, setLoadingOnboarding] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [onboardingForm, setOnboardingForm] = useState({
    name: '',
    subject: '',
    title: '',
    description: '',
    cta_text: 'Get Started',
    cta_url: 'https://baad.tv',
    days_after_signup: 0,
    enabled: true
  });
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [togglingTemplate, setTogglingTemplate] = useState(null);

  // Onboarding content blocks (Text + Image only — title is its own top-level field).
  const [onboardingBlocks, setOnboardingBlocks] = useState([
    { id: 'init', type: 'text', content: '' }
  ]);
  const [onboardingDraggedIndex, setOnboardingDraggedIndex] = useState(null);
  const [onboardingUploadingBlock, setOnboardingUploadingBlock] = useState(null);

  // Form state with content blocks
  const [formData, setFormData] = useState({
    subject: '',
    cta_text: 'Check it out',
    cta_url: 'https://baad.tv',
    category: 'feature_announcement'
  });

  const [contentBlocks, setContentBlocks] = useState([
    { id: '1', type: 'text', content: '' }
  ]);

  const [historyFilter, setHistoryFilter] = useState('all');
  const [uploadingBlock, setUploadingBlock] = useState(null);

  // Initial load
  useEffect(() => {
    loadEmailStats();
    loadAnnouncements();
    loadSchedulerStatus();
    loadDraft();
    loadOnboardingTemplates();
  }, []);

  // Auto-save draft
  const saveDraft = useCallback(() => {
    try {
      const draft = { formData, contentBlocks, savedAt: new Date().toISOString() };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
      setHasDraft(true);
    } catch (e) {
      console.error('Error saving draft:', e);
    }
  }, [formData, contentBlocks]);

  useEffect(() => {
    const hasContent = formData.subject || formData.title ||
      contentBlocks.some(b => b.content.trim());
    if (hasContent) {
      saveDraft();
    }
  }, [formData, contentBlocks, saveDraft]);

  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.formData) setFormData(draft.formData);
        if (draft.contentBlocks && draft.contentBlocks.length > 0) {
          setContentBlocks(draft.contentBlocks);
        }
        setHasDraft(true);
        toast.success('Draft loaded');
      }
    } catch (e) {
      console.error('Error loading draft:', e);
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setFormData({
      subject: '',
      cta_text: 'Check it out',
      cta_url: 'https://baad.tv',
      category: 'feature_announcement'
    });
    setContentBlocks([{ id: '1', type: 'text', content: '' }]);
    setHasDraft(false);
    toast.success('Draft cleared');
  };

  // Content block management
  const generateBlockId = () => `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addBlock = (type) => {
    let newBlock;
    if (type === 'title') {
      newBlock = { id: generateBlockId(), type, content: '', subContent: '' };
    } else {
      newBlock = { id: generateBlockId(), type, content: '' };
    }
    setContentBlocks([...contentBlocks, newBlock]);
  };

  const updateBlock = (id, content, field = 'content') => {
    setContentBlocks(contentBlocks.map(block =>
      block.id === id ? { ...block, [field]: content } : block
    ));
  };

  const removeBlock = (id) => {
    if (contentBlocks.length <= 1) {
      toast.error('You need at least one content block');
      return;
    }
    setContentBlocks(contentBlocks.filter(block => block.id !== id));
  };

  const moveBlock = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= contentBlocks.length) return;

    const newBlocks = [...contentBlocks];
    const [removed] = newBlocks.splice(index, 1);
    newBlocks.splice(newIndex, 0, removed);
    setContentBlocks(newBlocks);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newBlocks = [...contentBlocks];
    const [removed] = newBlocks.splice(draggedIndex, 1);
    newBlocks.splice(index, 0, removed);
    setContentBlocks(newBlocks);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleImageUpload = async (blockId, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingBlock(blockId);

    try {
      const fd = new FormData();
      fd.append('file', file);

      const response = await axios.post(`${API}/admin/email/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        updateBlock(blockId, response.data.url);
        toast.success('Image uploaded!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingBlock(null);
    }
  };

  const getDescriptionHtml = () => {
    return contentBlocks.map(block => {
      if (block.type === 'text') {
        return block.content;
      } else if (block.type === 'image' && block.content) {
        return `[IMAGE:${block.content}]`;
      } else if (block.type === 'title' && block.content) {
        if (block.subContent) {
          return `[TITLE:${block.content}|${block.subContent}]`;
        }
        return `[TITLE:${block.content}]`;
      }
      return '';
    }).filter(Boolean).join('\n\n');
  };

  // ============ Onboarding content blocks ============
  // Onboarding description is stored as plain text with [IMAGE:url] markers.
  // Parse/serialise between that format and a block array (Text + Image only).
  const parseOnboardingDescriptionToBlocks = (desc) => {
    if (!desc) return [{ id: generateBlockId(), type: 'text', content: '' }];
    const blocks = [];
    const parts = desc.split(/(\[IMAGE:[^\]]+\])/g);
    parts.forEach((part) => {
      if (!part) return;
      const m = part.match(/^\[IMAGE:(.+)\]$/);
      if (m) {
        blocks.push({ id: generateBlockId(), type: 'image', content: m[1] });
      } else {
        const trimmed = part.replace(/^\n+|\n+$/g, '');
        if (trimmed) {
          blocks.push({ id: generateBlockId(), type: 'text', content: trimmed });
        }
      }
    });
    if (blocks.length === 0) {
      blocks.push({ id: generateBlockId(), type: 'text', content: '' });
    }
    return blocks;
  };

  const serializeOnboardingBlocksToDescription = (blocks) => {
    return blocks.map((block) => {
      if (block.type === 'image' && block.content) return `[IMAGE:${block.content}]`;
      if (block.type === 'text') return block.content;
      return '';
    }).filter(Boolean).join('\n\n');
  };

  const addOnboardingBlock = (type) => {
    setOnboardingBlocks((prev) => [...prev, { id: generateBlockId(), type, content: '' }]);
  };

  const updateOnboardingBlock = (id, content) => {
    setOnboardingBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, content } : b)));
  };

  const removeOnboardingBlock = (id) => {
    setOnboardingBlocks((prev) => {
      if (prev.length <= 1) {
        toast.error('You need at least one content block');
        return prev;
      }
      return prev.filter((b) => b.id !== id);
    });
  };

  const moveOnboardingBlock = (index, direction) => {
    setOnboardingBlocks((prev) => {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      next.splice(newIndex, 0, removed);
      return next;
    });
  };

  const handleOnboardingDragStart = (e, index) => {
    setOnboardingDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleOnboardingDragOver = (e, index) => {
    e.preventDefault();
    if (onboardingDraggedIndex === null || onboardingDraggedIndex === index) return;
    setOnboardingBlocks((prev) => {
      const next = [...prev];
      const [removed] = next.splice(onboardingDraggedIndex, 1);
      next.splice(index, 0, removed);
      return next;
    });
    setOnboardingDraggedIndex(index);
  };

  const handleOnboardingDragEnd = () => setOnboardingDraggedIndex(null);

  const handleOnboardingImageUpload = async (blockId, file) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    setOnboardingUploadingBlock(blockId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/admin/email/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        updateOnboardingBlock(blockId, response.data.url);
        toast.success('Image uploaded!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setOnboardingUploadingBlock(null);
    }
  };

  const loadEmailStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/email/stats`, { withCredentials: true });
      setEmailStats(response.data);
    } catch (error) {
      console.error('Error loading email stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnnouncements = async (category = null) => {
    try {
      const categoryParam = category && category !== 'all' ? `&category=${category}` : '';
      const response = await axios.get(`${API}/admin/email/announcements?limit=20${categoryParam}`, { withCredentials: true });
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-scheduler/status`, { withCredentials: true });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error loading scheduler status:', error);
    }
  };

  const triggerEmailJob = async (jobName) => {
    setTriggeringJob(jobName);
    try {
      const response = await axios.post(`${API}/admin/email-scheduler/trigger/${jobName}`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success(response.data.message);
        loadAnnouncements(historyFilter);
        loadSchedulerStatus();
      }
    } catch (error) {
      console.error('Error triggering job:', error);
      toast.error(error.response?.data?.detail || 'Failed to trigger job');
    } finally {
      setTriggeringJob(null);
    }
  };

  const loadScheduledPreview = async (jobType) => {
    setLoadingScheduledPreview(jobType);
    try {
      const response = await axios.get(`${API}/admin/email/scheduled/${jobType}/preview`, { withCredentials: true });
      setScheduledPreview({ jobType, ...response.data });
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load preview');
    } finally {
      setLoadingScheduledPreview(null);
    }
  };

  const loadScheduledRecipients = async (jobType) => {
    setLoadingScheduledRecipients(jobType);
    try {
      const [activeRes, flaggedRes] = await Promise.all([
        axios.get(`${API}/admin/email/scheduled/${jobType}/recipients?limit=100&include_flagged=false`, { withCredentials: true }),
        axios.get(`${API}/admin/email/scheduled/${jobType}/recipients?limit=100&include_flagged=true`, { withCredentials: true })
      ]);
      setScheduledRecipients({ jobType, ...activeRes.data });
      setScheduledFlagged({ jobType, ...flaggedRes.data });
      setScheduledRecipientTab('active');
    } catch (error) {
      console.error('Error loading recipients:', error);
      toast.error('Failed to load recipients');
    } finally {
      setLoadingScheduledRecipients(null);
    }
  };

  const sendScheduledTest = async (jobType) => {
    setSendingScheduledTest(jobType);
    try {
      const response = await axios.post(`${API}/admin/email/scheduled/${jobType}/send-test`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingScheduledTest(null);
    }
  };

  const loadManualRecipients = async () => {
    setLoadingManualRecipients(true);
    try {
      const [activeRes, flaggedRes] = await Promise.all([
        axios.get(`${API}/admin/email/manual-recipients?limit=100&include_flagged=false`, { withCredentials: true }),
        axios.get(`${API}/admin/email/manual-recipients?limit=100&include_flagged=true`, { withCredentials: true })
      ]);
      setManualRecipients(activeRes.data);
      setManualFlagged(flaggedRes.data);
      setRecipientTab('active');
    } catch (error) {
      console.error('Error loading recipients:', error);
      toast.error('Failed to load recipients');
    } finally {
      setLoadingManualRecipients(false);
    }
  };

  const flagUser = async (userId, shouldFlag) => {
    setFlaggingUser(userId);
    try {
      const endpoint = shouldFlag ? 'flag-user' : 'unflag-user';
      await axios.post(`${API}/admin/email/${endpoint}/${userId}`, {}, { withCredentials: true });
      toast.success(shouldFlag ? 'User flagged as test user' : 'User unflagged');
      loadManualRecipients();
    } catch (error) {
      console.error('Error flagging user:', error);
      toast.error('Failed to update user');
    } finally {
      setFlaggingUser(null);
    }
  };

  const handleHistoryFilterChange = (category) => {
    setHistoryFilter(category);
    loadAnnouncements(category);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getFormDataWithDescription = () => ({
    ...formData,
    description: getDescriptionHtml()
  });

  const handlePreview = async () => {
    const description = getDescriptionHtml();
    if (!formData.subject || !description) {
      toast.error('Please fill in subject and at least one content block');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/email/preview`, getFormDataWithDescription(), { withCredentials: true });
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    }
  };

  const handleSendTest = async () => {
    const description = getDescriptionHtml();
    if (!formData.subject || !description) {
      toast.error('Please fill in subject and at least one content block');
      return;
    }

    setSendingTest(true);
    try {
      const response = await axios.post(`${API}/admin/email/send-test`, getFormDataWithDescription(), { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendToAll = async () => {
    const description = getDescriptionHtml();
    if (!formData.subject || !description) {
      toast.error('Please fill in subject and at least one content block');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to send this announcement to ${emailStats?.recipients?.total_active || 0} users?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/email/send-announcement`, getFormDataWithDescription(), { withCredentials: true });

      if (response.data.success) {
        toast.success(`Email sent to ${response.data.sent} recipients!`);
        clearDraft();
        loadEmailStats();
        loadAnnouncements(historyFilter);
      } else {
        toast.error(response.data.message || 'Failed to send announcement');
      }
    } catch (error) {
      console.error('Error sending announcement:', error);
      toast.error(error.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Onboarding templates
  const loadOnboardingTemplates = async () => {
    setLoadingOnboarding(true);
    try {
      const response = await axios.get(`${API}/admin/email/onboarding/templates`, { withCredentials: true });
      setOnboardingTemplates(response.data);
    } catch (error) {
      console.error('Error loading onboarding templates:', error);
    } finally {
      setLoadingOnboarding(false);
    }
  };

  const openOnboardingModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setOnboardingForm({
        name: template.name,
        subject: template.subject,
        title: template.title,
        description: template.description,
        cta_text: template.cta_text || 'Get Started',
        cta_url: template.cta_url || 'https://baad.tv',
        days_after_signup: template.days_after_signup,
        enabled: template.enabled
      });
      setOnboardingBlocks(parseOnboardingDescriptionToBlocks(template.description));
    } else {
      setEditingTemplate(null);
      setOnboardingForm({
        name: '',
        subject: '',
        title: '',
        description: '',
        cta_text: 'Get Started',
        cta_url: 'https://baad.tv',
        days_after_signup: 0,
        enabled: true
      });
      setOnboardingBlocks([{ id: generateBlockId(), type: 'text', content: '' }]);
    }
    setShowOnboardingModal(true);
  };

  const saveOnboardingTemplate = async () => {
    const description = serializeOnboardingBlocksToDescription(onboardingBlocks).trim();
    if (!onboardingForm.name || !onboardingForm.subject || !onboardingForm.title || !description) {
      toast.error('Please fill in all required fields (and at least one content block)');
      return;
    }

    const payload = { ...onboardingForm, description };

    setSavingOnboarding(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/admin/email/onboarding/templates/${editingTemplate.id}`, payload, { withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/admin/email/onboarding/templates`, payload, { withCredentials: true });
        toast.success('Template created');
      }
      setShowOnboardingModal(false);
      setEditingTemplate(null);
      await loadOnboardingTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', '));
      } else {
        toast.error(detail || 'Failed to save template');
      }
    } finally {
      setSavingOnboarding(false);
    }
  };

  const deleteOnboardingTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API}/admin/email/onboarding/templates/${templateId}`, { withCredentials: true });
      toast.success('Template deleted');
      loadOnboardingTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const toggleOnboardingTemplate = async (templateId) => {
    setTogglingTemplate(templateId);
    try {
      const response = await axios.post(`${API}/admin/email/onboarding/templates/${templateId}/toggle`, {}, { withCredentials: true });
      toast.success(response.data.enabled ? 'Template enabled' : 'Template disabled');
      loadOnboardingTemplates();
    } catch (error) {
      console.error('Error toggling template:', error);
      toast.error('Failed to toggle template');
    } finally {
      setTogglingTemplate(null);
    }
  };

  const testOnboardingTemplate = async (templateId) => {
    try {
      const response = await axios.post(`${API}/admin/email/onboarding/templates/${templateId}/test`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error('Failed to send test');
    }
  };

  const ctxValue = {
    // shared meta
    emailCategories: EMAIL_CATEGORIES,
    formatDate,

    // stats / lists
    emailStats, loading,
    announcements,
    schedulerStatus,
    historyFilter, handleHistoryFilterChange,

    // compose state + handlers
    formData, setFormData, handleInputChange,
    contentBlocks, setContentBlocks,
    draggedIndex, handleDragStart, handleDragOver, handleDragEnd,
    addBlock, updateBlock, removeBlock, moveBlock,
    uploadingBlock, handleImageUpload,
    hasDraft, clearDraft,
    handlePreview, handleSendTest, handleSendToAll,
    sending, sendingTest,

    // scheduler
    triggerEmailJob, triggeringJob,
    loadScheduledPreview, loadingScheduledPreview,
    loadScheduledRecipients, loadingScheduledRecipients,
    sendScheduledTest, sendingScheduledTest,
    scheduledPreview, setScheduledPreview,
    scheduledRecipients, setScheduledRecipients,
    scheduledFlagged, setScheduledFlagged,
    scheduledRecipientTab, setScheduledRecipientTab,

    // recipients (manual)
    loadManualRecipients, loadingManualRecipients,
    manualRecipients, setManualRecipients,
    manualFlagged, setManualFlagged,
    recipientTab, setRecipientTab,
    flagUser, flaggingUser,

    // onboarding
    onboardingTemplates, loadingOnboarding,
    openOnboardingModal, deleteOnboardingTemplate,
    toggleOnboardingTemplate, togglingTemplate,
    testOnboardingTemplate,
    showOnboardingModal, setShowOnboardingModal,
    editingTemplate, onboardingForm, setOnboardingForm,
    saveOnboardingTemplate, savingOnboarding,
    // onboarding content blocks
    onboardingBlocks,
    onboardingDraggedIndex,
    onboardingUploadingBlock,
    addOnboardingBlock,
    updateOnboardingBlock,
    removeOnboardingBlock,
    moveOnboardingBlock,
    handleOnboardingDragStart,
    handleOnboardingDragOver,
    handleOnboardingDragEnd,
    handleOnboardingImageUpload,
  };

  return (
    <>
      <SEO
        title="Email Management | Admin | baad.tv"
        description="Manage email communications for baad.tv"
        url="/admin/email"
      />
      <div className="admin-email-page" data-testid="admin-email-page">
        <Navigation user={user} setUser={setUser} currentPage="admin" />

        <div className="admin-email-content">
          <div className="page-header">
            <Link to="/admin" className="back-link">
              <ArrowLeft size={20} />
              Back to Admin
            </Link>
            <h1><Mail className="header-icon" /> Email Management</h1>
            <p>Send announcements and manage email communications</p>
          </div>

          <div className="stats-bar">
            <div className="stat-item primary">
              <Users size={18} />
              <div>
                <span className="stat-value">{emailStats?.recipients?.total_active || 0}</span>
                <span className="stat-label">Active Recipients</span>
              </div>
            </div>
            <div className="stat-item">
              <UserCheck size={18} />
              <div>
                <span className="stat-value">{emailStats?.recipients?.opted_in || 0}</span>
                <span className="stat-label">Opted In</span>
              </div>
            </div>
            <div className="stat-item muted">
              <UserX size={18} />
              <div>
                <span className="stat-value">{emailStats?.recipients?.opted_out || 0}</span>
                <span className="stat-label">Opted Out</span>
              </div>
            </div>
            <div className="stat-item flagged">
              <UserX size={18} />
              <div>
                <span className="stat-value">{emailStats?.recipients?.flagged || 0}</span>
                <span className="stat-label">Flagged / Test</span>
              </div>
            </div>
            <div className="stat-item">
              <Send size={18} />
              <div>
                <span className="stat-value">{emailStats?.announcements?.total_sent || 0}</span>
                <span className="stat-label">Emails Sent</span>
              </div>
            </div>
          </div>

          <div className="tabs-container">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>

          <AdminEmailProvider value={ctxValue}>
            <div className="tab-content">
              {activeTab === 'compose' && <ComposeTab />}
              {activeTab === 'onboarding' && <OnboardingTab />}
              {activeTab === 'nudges' && <NudgesTab />}
              {activeTab === 'history' && <HistoryTab />}
              {activeTab === 'scheduler' && <SchedulerTab />}
              {activeTab === 'recipients' && <RecipientsTab />}
              {activeTab === 'analytics' && <AnalyticsTab />}
            </div>

            <ManualRecipientsModal />
            <ScheduledRecipientsModal />
          </AdminEmailProvider>
        </div>

        {showPreview && (
          <div className="preview-modal-overlay" onClick={() => setShowPreview(false)}>
            <div className="preview-modal" onClick={e => e.stopPropagation()}>
              <div className="preview-header">
                <h3>Email Preview</h3>
                <button onClick={() => setShowPreview(false)} className="close-btn">×</button>
              </div>
              <div className="preview-subject">
                <strong>Subject:</strong> {formData.subject}
              </div>
              <div
                className="preview-content"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminEmail;
